/**
 * First-time nav gating: the main land starts with NOTHING but the land, its
 * buildings and the mission button, and each HUD icon is revealed one at a time
 * at the moment the story actually needs it. A brand-new player is never asked
 * to ignore a dozen buttons they have no context for.
 *
 * Reveal schedule (day 1) — each of these is paired with an arrival bounce and a
 * narrator explanation at the point of reveal:
 *
 *   quest 5  (fight a battle) → PvE Map        "this is where you fight"
 *   quest 8  (craft, once done) → Shop         "materials can be bought"
 *   quest 10 (palace)          → Daily ribbon + Reward
 *                                 daily quests pay out, and the Reward icon is
 *                                 where longer-running prizes are collected
 *   quest 12 (win a battle)    → Inventory, then Characters
 *                                 the Claymore sits in the inventory; the
 *                                 Characters page is where it gets equipped, so
 *                                 the bag must be introduced first
 *
 * Everything else (World Map/MvM, PvP, Premium, Guide, Events, Notifications,
 * era) is revealed by the closing land tour once the last day-1 quest is
 * claimed — see FtueLandIconTour, which walks them one by one.
 */

import { useSelector } from 'react-redux'
import { useFtueGuideChoice } from './useFtueGate'
import type { RootState } from '../store'
import type { Mission, MissionProgress } from '../common/state/mission/types'

export interface FtueNavVisibility {
  map: boolean
  notifications: boolean
  worldMap: boolean
  pvp: boolean
  inventory: boolean
  characters: boolean
  shop: boolean
  reward: boolean
  premium: boolean
  guide: boolean
  events: boolean
  era: boolean
  dailyBanner: boolean
}

const ALL_VISIBLE: FtueNavVisibility = {
  map: true,
  notifications: true,
  worldMap: true,
  pvp: true,
  inventory: true,
  characters: true,
  shop: true,
  reward: true,
  premium: true,
  guide: true,
  events: true,
  era: true,
  dailyBanner: true,
}

const ALL_HIDDEN: FtueNavVisibility = {
  map: false,
  notifications: false,
  worldMap: false,
  pvp: false,
  inventory: false,
  characters: false,
  shop: false,
  reward: false,
  premium: false,
  guide: false,
  events: false,
  era: false,
  dailyBanner: false,
}

/** RootState does not surface the mission slice, so widen it here — once —
 *  instead of casting to `any` at every call site. */
type MissionRootState = RootState & { app: { mission: MissionSlice } }

interface MissionSlice {
  currentMission: Mission | null
  /** The DAY TEMPLATE (all 12 quests). The mission row above only holds quests
   *  the player has actually started, so it can never say how many there are. */
  currentMissionData: { missionDay?: number; quests?: unknown[] } | null
  missionProgress: MissionProgress | null
}

/** True while the day-1 story is still running (drives the reveal schedule). */
export function useFtueDayOneActive(): boolean {
  const { currentMission, currentMissionData, missionProgress } = useSelector(
    (state: RootState) => (state as MissionRootState).app.mission,
  )
  if (!missionProgress || missionProgress.missionDay !== 1) return false

  // The mission ROW is a progress log: it only contains quests the player has
  // reached. Comparing "every entry claimed" against it declared day 1 finished
  // after the very first quest — which retired the guide two quests in. The day
  // is over only when the row holds the WHOLE template and all of it is claimed.
  const total =
    currentMissionData?.missionDay === 1 ? currentMissionData?.quests?.length ?? 0 : 0
  const rowQuests = currentMission?.missionDay === 1 ? currentMission.quests ?? [] : []
  if (total === 0) return true // template not loaded yet → assume still running
  return !(rowQuests.length >= total && rowQuests.every((q) => q.claimed))
}

export function useFtueNavVisibility(): FtueNavVisibility {
  const { currentMission, currentMissionData, missionProgress } = useSelector(
    (state: RootState) => (state as MissionRootState).app.mission,
  )
  // Declining the guide hands the whole HUD over immediately — a player who
  // opted out must not be left with a stripped-down screen.
  const { over } = useFtueGuideChoice()
  if (over) return ALL_VISIBLE

  // Mission state not loaded yet: keep the HUD blank rather than flashing the
  // full button set at a brand-new player; veterans see the icons the moment
  // their (day > 1) progress arrives.
  if (!missionProgress) return ALL_HIDDEN
  if (missionProgress.missionDay !== 1) return ALL_VISIBLE

  const quests = currentMission?.missionDay === 1 ? currentMission.quests : null
  // Day-1 story finished → hand the full HUD over. Judged against the day
  // TEMPLATE, never the row: the row only holds quests the player has started,
  // so "every entry claimed" was true after quest 1 and dumped the entire HUD
  // on the player two quests in, defeating the one-at-a-time reveal.
  const dayOneTotal =
    currentMissionData?.missionDay === 1 ? currentMissionData?.quests?.length ?? 0 : 0
  if (
    dayOneTotal > 0 &&
    (quests?.length ?? 0) >= dayOneTotal &&
    !!quests?.every((q) => q.claimed)
  ) {
    return ALL_VISIBLE
  }

  const questId = missionProgress.questId ?? 1
  const quest8 = quests?.find((q) => q.id === 8)

  return {
    ...ALL_HIDDEN,
    map: questId >= 5,
    shop: questId > 8 || (questId === 8 && !!quest8?.completed),
    dailyBanner: questId >= 10,
    reward: questId >= 10,
    inventory: questId >= 12,
    characters: questId >= 12,
  }
}
